/**
 * Global Authentication Refresh Manager
 *
 * Prevents race conditions by ensuring only one refresh operation
 * can be in progress at a time across the entire application.
 *
 * Used by both AuthContext and tRPC client to coordinate refreshes.
 */

import { authApi } from "./auth";

interface RefreshResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

class AuthRefreshManager {
  private refreshPromise: Promise<RefreshResult> | null = null;
  private isRefreshing = false;

  /**
   * Perform a token refresh, ensuring only one refresh happens at a time
   * @param refreshToken - The refresh token to use
   * @param cognitoRefreshToken - The Cognito refresh token to use
   * @param deviceFingerprint - Device fingerprint for security validation
   * @returns Promise that resolves to the new tokens
   */
  async refresh(
    refreshToken: string,
    cognitoRefreshToken: string,
    deviceFingerprint: string,
  ): Promise<RefreshResult> {
    // If a refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Mark as refreshing and create the promise
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh(refreshToken, cognitoRefreshToken, deviceFingerprint);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Clean up state regardless of success/failure
      this.refreshPromise = null;
      this.isRefreshing = false;
    }
  }

  /**
   * Check if a refresh is currently in progress
   */
  get isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }

  /**
   * Wait for any ongoing refresh to complete
   * Returns null if no refresh is in progress
   */
  async waitForRefresh(): Promise<RefreshResult | null> {
    if (this.refreshPromise) {
      try {
        return await this.refreshPromise;
      } catch {
        // Refresh failed, but we still waited for it
        return null;
      }
    }
    return null;
  }

  /**
   * Internal method to perform the actual refresh
   */
  private async performRefresh(
    refreshToken: string,
    cognitoRefreshToken: string,
    deviceFingerprint: string,
  ): Promise<RefreshResult> {
    const response = await authApi.refresh(refreshToken, cognitoRefreshToken, deviceFingerprint);
    return response.data;
  }

  /**
   * Reset the refresh state (used for testing or error recovery)
   */
  reset(): void {
    this.refreshPromise = null;
    this.isRefreshing = false;
  }
}

// Export singleton instance
export const authRefreshManager = new AuthRefreshManager();

// Export type for use in other files
export type { RefreshResult };
